import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SideMenu, MainContainer, type NavGroup, type NavItem } from '../components/aip';
import { useAuth } from '../contexts/AuthContext';

// スタイルをインポート
import '../styles/aip/index.css';

export interface ManageNewLayoutProps {
  /** カスタムナビゲーション（指定しない場合はデフォルトを使用） */
  navigation?: NavGroup[];
  /** 子要素（Outletを使用しない場合） */
  children?: ReactNode;
}

// ベースのナビゲーション設定（常に表示）
const baseMenuItems: NavItem[] = [
  {
    label: 'ダッシュボード',
    path: '/meeting/manage-new',
    icon: 'dashboard',
  },
  {
    label: 'AIマネージャー',
    path: '/meeting/manage-new/ai-managers',
    icon: 'smart_toy',
  },
  {
    label: 'チェックテンプレート',
    path: '/meeting/manage-new/check-templates',
    icon: 'checklist',
  },
  {
    label: '議事録',
    path: '/meeting/manage-new/minutes',
    icon: 'description',
  },
];

// 管理者専用メニュー
const adminOnlyItems: NavItem[] = [
  {
    label: 'ユーザ',
    path: '/meeting/manage-new/admin-users',
    icon: 'group',
  },
];

// その他メニュー
const otherMenuItems: NavItem[] = [
  {
    label: '設定',
    path: '/meeting/manage-new/settings',
    icon: 'settings',
  },
];

/**
 * 管理画面の共通レイアウトコンポーネント
 */
export const ManageNewLayout: React.FC<ManageNewLayoutProps> = ({
  navigation,
  children,
}) => {
  const location = useLocation();
  const { user } = useAuth();

  // isAdmin に応じてナビゲーションを構築
  const computedNavigation = useMemo<NavGroup[]>(() => {
    // カスタムナビゲーションが渡された場合はそれを使用
    if (navigation) {
      return navigation;
    }

    // デフォルトナビゲーションを構築
    const menuItems = [...baseMenuItems];
    
    // 管理者の場合のみユーザメニューを追加
    if (user?.isAdmin) {
      menuItems.push(...adminOnlyItems);
    }

    return [
      {
        header: '管理メニュー',
        items: menuItems,
      },
      {
        header: 'その他',
        items: otherMenuItems,
      },
    ];
  }, [navigation, user?.isAdmin]);
  // 初期値はFOUC対策スクリプト（index.html）で設定されたdata-menu-stateから取得
  const getInitialMenuState = (): 'open' | 'closed' => {
    const htmlState = document.documentElement.getAttribute('data-menu-state');
    if (htmlState === 'open' || htmlState === 'closed') {
      return htmlState;
    }
    return window.innerWidth <= 1050 ? 'closed' : 'open';
  };
  const [menuState, setMenuState] = useState<'open' | 'closed'>(getInitialMenuState);

  // 画面幅に応じて状態を更新
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1050) {
        setMenuState('closed');
      } else {
        setMenuState('open');
      }
    };

    // 初回は実行しない（FOUC対策スクリプトで既に設定済み）

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // モバイルでページ遷移時にメニューを閉じる
  useEffect(() => {
    if (window.innerWidth <= 1050) {
      setMenuState('closed');
    }
  }, [location.pathname]);

  const toggleMenu = () => {
    setMenuState((prev) => (prev === 'open' ? 'closed' : 'open'));
  };

  const handleOverlayClick = () => {
    setMenuState('closed');
  };

  // ページタイトルを取得
  const getPageTitle = () => {
    const path = location.pathname;
    for (const group of computedNavigation) {
      for (const item of group.items) {
        if (path === item.path || (path.startsWith(item.path) && item.path !== '/meeting/manage-new')) {
          return item.label;
        }
      }
    }
    return 'ダッシュボード';
  };

  // 一覧ページかどうかを判定（一覧ページは横幅いっぱいに表示）
  const isListPage = () => {
    const path = location.pathname;
    // 一覧ページのパスパターン（末尾に/new, /:id, /:id/editがない）
    const listPaths = [
      '/meeting/manage-new/ai-managers',
      '/meeting/manage-new/check-templates',
      '/meeting/manage-new/minutes',
      '/meeting/manage-new/admin-users',
    ];
    return listPaths.some((listPath) => path === listPath || path === `${listPath}/`);
  };

  // ページサイズを取得（一覧ページはlg、それ以外はsm）
  const getPageSize = () => {
    return isListPage() ? 'lg' : 'sm';
  };

  return (
    <div className="aip-html-body">
      {/* メニュートグルボタン */}
      <button
        className="aip-side-menu__toggle"
        onClick={toggleMenu}
        aria-label={menuState === 'open' ? 'メニューを閉じる' : 'メニューを開く'}
      >
        <span className="material-symbols-outlined">dock_to_right</span>
      </button>

      {/* モバイル用オーバーレイ */}
      <div
        className="aip-side-menu__overlay"
        onClick={handleOverlayClick}
        style={{
          opacity: menuState === 'open' ? 1 : 0,
          visibility: menuState === 'open' ? 'visible' : 'hidden',
        }}
      />

      {/* メインレイアウトコンテナ */}
      <div className="aip-layout" data-menu-state={menuState}>
        {/* サイドメニュー */}
        <SideMenu navigation={computedNavigation} />

        {/* メインコンテンツ */}
        <MainContainer title={getPageTitle()} size={getPageSize()}>
          {children || <Outlet />}
        </MainContainer>
      </div>
    </div>
  );
};

export default ManageNewLayout;

